import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Switch from 'antd/lib/switch';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { globalMessage } from '@/utils/globalMessage';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import Checkbox from 'antd/lib/checkbox';
import {
    selectRequiresFixRequestsApprovals,
    selectVCSSupportsStatusChecks,
    selectEnableStatusChecksForFixRequests,
    selectAddFixRequestsToOpenPRsAgainstReleaseBranches,
    selectThreadFixNotifications,
    selectRequiresFixRequestsDetails,
    selectFixRequestsDetailsTemplate,
    selectNotificationIntegration,
    selectFixRequestsAutoMergeUserCreatedPRsSettings,
} from '@/selectors/selectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Divider from 'antd/lib/divider';
import { RootState } from 'reducers/reducers';
import { MergeType, IntegrationType } from '@/types';
import { Select, Space, Tooltip } from 'antd/lib';
import { Option } from 'antd/lib/mentions';
import { useWatch } from 'antd/es/form/Form';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { InfoCircleOutlined } from '@ant-design/icons';

interface EditFixesSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditFixesSettingsModal: React.FC<EditFixesSettingsModalProps> = (props: EditFixesSettingsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const requiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const vcsSupportsStatusChecks = useSelector(selectVCSSupportsStatusChecks(props.appId, props.orgId));
    const enableStatusChecksForFixRequests = useSelector(selectEnableStatusChecksForFixRequests(props.appId));
    const threadFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const requiresFixRequestsDetails = useSelector(selectRequiresFixRequestsDetails(props.appId));
    const fixRequestsDetailsTemplate = useSelector(selectFixRequestsDetailsTemplate(props.appId));
    const { mergeUserCreatedPRs, workingBranchUserCreatedPRMergeType, releaseBranchUserCreatedPRMergeType } =
        useSelector(selectFixRequestsAutoMergeUserCreatedPRsSettings(props.appId));

    const editFixesSettings = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            addFixRequestsToOpenPRsAgainstReleaseBranches: value.addFixRequestsToOpenPRsAgainstReleaseBranches,
            requiresFixRequestsApprovals: value.requireApprovals,
            enableStatusChecksForFixRequests: value.requireApprovals && value.enableStatusChecks,
            requiresFixRequestsDetails: value.requireDetails,
            fixRequestsDetailsTemplate: value.detailsTemplate,
            threadFixNotifications: value.threadFixNotifications,
            mergeUserCreatedPRs: value.mergeUserCreatedPRs,
            workingBranchUserCreatedPRMergeType: value.workingBranchUserCreatedPRMergeType ?? [],
            releaseBranchUserCreatedPRMergeType: value.releaseBranchUserCreatedPRMergeType,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    const updateFixesFeatureSettingsIsLoading = useIsLoading(AppSettingsUpdatable.RequiresFixRequestsApprovals);
    const updateFixesFeatureSettingsSuccess = useUpdated(AppSettingsUpdatable.RequiresFixRequestsApprovals);
    const updateFixesFeatureSettingsError = useError(AppSettingsUpdatable.RequiresFixRequestsApprovals);

    const notificationIntegration = useSelector((state: RootState) =>
        selectNotificationIntegration(state, props.appId),
    );
    const isMergeUserCreatedPRsEnabled = useWatch('mergeUserCreatedPRs', form);

    // threading is only supported in Slack
    const hasSlackIntegration =
        notificationIntegration != null ? notificationIntegration.id === IntegrationType.Slack : false;

    React.useEffect(() => {
        if (updateFixesFeatureSettingsSuccess) {
            globalMessage.success('Fixes settings updated successfully!');
            props.onCancel();
        }
    }, [updateFixesFeatureSettingsSuccess]);

    React.useEffect(() => {
        if (updateFixesFeatureSettingsError) {
            globalMessage.error(updateFixesFeatureSettingsError);
        }
    }, [updateFixesFeatureSettingsError]);

    const initialValues = {
        addFixRequestsToOpenPRsAgainstReleaseBranches: addFixRequestsToOpenPRsAgainstReleaseBranches,
        requireApprovals: requiresFixRequestsApprovals,
        enableStatusChecks: enableStatusChecksForFixRequests,
        threadFixNotifications: threadFixNotifications,
        requireDetails: requiresFixRequestsDetails,
        detailsTemplate: fixRequestsDetailsTemplate,
        mergeUserCreatedPRs: mergeUserCreatedPRs,
        workingBranchUserCreatedPRMergeType: workingBranchUserCreatedPRMergeType,
        releaseBranchUserCreatedPRMergeType: releaseBranchUserCreatedPRMergeType,
    };

    const displayMergeStrategyValue = (strategy: MergeType) => {
        switch (strategy) {
            case MergeType.DoNotMerge:
                return 'Do not merge user-created PRs';
            case MergeType.MergeCommit:
                return 'Merge user-created PRs with merge commit';
            case MergeType.Squash:
                return 'Squash merge user-created PRs';
        }
    };

    const mergeStrategyLabel = (targetBranch: string) => (
        <Text
            style={{
                color: isMergeUserCreatedPRsEnabled ? runwayColors.black : antGrey.grey5,
            }}
        >
            {targetBranch} branch
        </Text>
    );

    const mergeStrategyOptions = Object.values(MergeType).map((strategy) => {
        return (
            <Option value={strategy} key={strategy}>
                {displayMergeStrategyValue(strategy)}
            </Option>
        );
    });

    const tooltipForFormField = (helperText: string) => {
        return {
            title: <Text style={{ color: runwayColors.white }}>{helperText}</Text>,
            icon: (
                <Space style={{ marginLeft: 8, cursor: 'default' }}>
                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                </Space>
            ),
        };
    };

    return (
        <Modal
            title="Edit fixes settings"
            onOk={form.submit}
            okButtonProps={{ loading: updateFixesFeatureSettingsIsLoading }}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                form.resetFields();
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    These settings will be used by Runway in regards to late-arriving work items that may still be
                    pulled into a release.
                </Text>
                <Form
                    onFinish={editFixesSettings}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="addFixRequestsToOpenPRsAgainstReleaseBranches"
                        valuePropName="checked"
                        label={'Create a fix for all open PRs against the release branch'}
                        tooltip={tooltipForFormField(
                            'A fix will be automatically created for any PRs opened against the release branch post-kickoff.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="requireApprovals"
                        valuePropName="checked"
                        label={'Fix approvals'}
                        tooltip={tooltipForFormField(
                            'If enabled, fix requests can be approved or rejected by team members with sufficient privileges. Approvals can be performed on the Feature readiness step.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item
                        style={{ marginTop: 40 }}
                        name="enableStatusChecks"
                        hidden={!vcsSupportsStatusChecks}
                        valuePropName="checked"
                        tooltip={tooltipForFormField('')}
                    >
                        <Checkbox>Add pull request status check for fix request approvals</Checkbox>
                        <Tooltip title="Runway will include a status check on any pull requests created for fixes going into the release branch. The status check will only pass when the fix request associated with the PR has been approved.">
                            <InfoCircleOutlined
                                style={{
                                    color: antGrey.grey7,
                                }}
                            />
                        </Tooltip>
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="requireDetails"
                        valuePropName="checked"
                        label={'Require details'}
                        tooltip={tooltipForFormField(
                            'If enabled, all manually created fix requests will require non-empty details.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="mergeUserCreatedPRs"
                        valuePropName="checked"
                        label={'Auto-merge user-created PRs with fixes'}
                        tooltip={tooltipForFormField(
                            'If enabled, user-created PRs with fixes against the selected target branches will be automatically merged if they are approved and have no conflicts.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item name="workingBranchUserCreatedPRMergeType" label={mergeStrategyLabel('Working')}>
                        <Select disabled={!isMergeUserCreatedPRsEnabled} style={{ width: '100%' }}>
                            {mergeStrategyOptions}
                        </Select>
                    </Form.Item>
                    <Form.Item name="releaseBranchUserCreatedPRMergeType" label={mergeStrategyLabel('Release')}>
                        <Select disabled={!isMergeUserCreatedPRsEnabled} style={{ width: '100%' }}>
                            {mergeStrategyOptions}
                        </Select>
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="detailsTemplate"
                        label={'Details template'}
                        tooltip={tooltipForFormField(
                            'This text will be pre-populated in the fix request form in Runway and in notifications.',
                        )}
                    >
                        <Input.TextArea rows={5} />
                    </Form.Item>
                    <Divider />
                    {hasSlackIntegration && (
                        <Form.Item
                            name="threadFixNotifications"
                            valuePropName="checked"
                            label={'Thread related fix notifications'}
                            tooltip={tooltipForFormField(
                                'Notifications relevant to a given fix will be threaded in Slack',
                            )}
                        >
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default EditFixesSettingsModal;
